import { useState } from "react";
import { Button } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { setAuthToken } from "../utils/tokenHeaders";
import { useSelector } from "react-redux";
import AppPageShell from "./AppPageShell";
import { KEY_TYPES, PRICING_FAQ, PRICING_SECTIONS } from "../content/pricing";
import { formatSubscriptionExpiry } from "../utils/userProfile";

const SUPPORT_EMAIL = "support@club-lab.ru";

const prodURL = "/api";
const url =
  window.location.hostname === "localhost"
    ? "http://localhost:3004/api"
    : prodURL;

function formatPrice(value) {
  return new Intl.NumberFormat("ru-RU").format(value);
}

function KeyTypeVisual({ typeId }) {
  if (typeId === "single") {
    return (
      <div className="app-price-type__visual" aria-hidden="true">
        <div className="app-price-type__visual-source">
          <VpnKeyOutlinedIcon />
        </div>
        <ArrowForwardIcon className="app-price-type__visual-arrow" />
        <div className="app-price-type__visual-target">
          <PersonOutlineIcon />
        </div>
      </div>
    );
  }

  return (
    <div className="app-price-type__visual" aria-hidden="true">
      <div className="app-price-type__visual-source app-price-type__visual-source--group">
        <LinkOutlinedIcon />
      </div>
      <ArrowForwardIcon className="app-price-type__visual-arrow" />
      <div className="app-price-type__visual-target app-price-type__visual-target--group">
        <GroupsOutlinedIcon />
      </div>
    </div>
  );
}

function KeyTypeChoice({ type, onSelect }) {
  return (
    <button
      type="button"
      className={`app-price-type app-price-type--${type.id}`}
      onClick={() => onSelect(type.id)}
    >
      <KeyTypeVisual typeId={type.id} />
      <h3 className="app-price-type__title">{type.title}</h3>
      <p className="app-price-type__summary">{type.summary}</p>
      <p className="app-price-type__audience">
        <strong>Кому подходит:</strong> {type.audience}
      </p>
      <ul className="app-price-type__points">
        {type.points.map((point) => (
          <li key={point}>{point}</li>
        ))}
      </ul>
      <span className="app-price-type__action">Выбрать</span>
    </button>
  );
}

function PricingCard({ plan, onBuy, disabled }) {
  const isContactOnly = plan.contactOnly;

  return (
    <div
      className={`app-price-card${
        plan.highlight ? " app-price-card--featured" : ""
      }${isContactOnly ? " app-price-card--contact" : ""}`}
    >
      {plan.highlight ? (
        <span className="app-price-card__tag">Популярный</span>
      ) : null}
      <div className="app-price-card__title">{plan.title}</div>
      {plan.segmentLabel ? (
        <div className="app-price-card__segment-inline">{plan.segmentLabel}</div>
      ) : null}
      <div className="app-price-card__badge">{plan.badge}</div>
      {!isContactOnly ? (
        <div className="app-price-card__price">
          {formatPrice(plan.price)} &#8381;
          {plan.pricePeriod ? (
            <span className="app-price-card__period">{plan.pricePeriod}</span>
          ) : null}
        </div>
      ) : (
        <div className="app-price-card__price app-price-card__price--contact">
          По запросу
        </div>
      )}
      <div className="app-price-card__per-unit">{plan.perUnit}</div>
      <ul className="app-price-card__features">
        {plan.features.map((feature) => (
          <li key={feature}>{feature}</li>
        ))}
      </ul>
      {isContactOnly ? (
        <Button
          variant="outlined"
          className="app-btn app-price-card__cta"
          component="a"
          href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
            "Групповой доступ 100+ сотрудников — MOGAME"
          )}`}
        >
          Написать нам
        </Button>
      ) : (
        <Button
          variant="contained"
          className="app-btn app-btn--primary app-price-card__cta"
          onClick={() => onBuy(plan.packageId)}
          disabled={disabled}
        >
          {plan.pricePeriod ? "Оформить" : "Купить"}
        </Button>
      )}
    </div>
  );
}

function formatBusinessPlanLabel(subscription) {
  if (!subscription?.label) return null;
  const max = subscription.teamSizeMax;
  return max ? `${subscription.label} (до ${max} чел.)` : subscription.label;
}

export default function Price() {
  const user = useSelector((state) => state.user?.data);
  const profile = useSelector((state) => state.user?.profile);
  const [selectedKeyType, setSelectedKeyType] = useState(null);
  const [faqOpen, setFaqOpen] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const [error, setError] = useState(null);

  const singleKeys = profile?.limit ?? user?.user?.limit ?? 0;
  const businessSubscription =
    profile?.businessSubscription ?? user?.user?.businessSubscription ?? null;
  const businessUntil = formatSubscriptionExpiry(businessSubscription);
  const businessPlan = formatBusinessPlanLabel(businessSubscription);

  const activeSection = PRICING_SECTIONS.find(
    (section) => section.keyType === selectedKeyType,
  );
  const activeKeyType = KEY_TYPES.find((type) => type.id === selectedKeyType);

  const handlePayment = async (packageId) => {
    setError(null);
    setLoadingId(packageId);

    try {
      const response = await fetch(`${url}/payment/create-payment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: setAuthToken(),
        },
        body: JSON.stringify({
          id: user?.user?.id,
          packageId,
        }),
      });

      const result = await response.json();

      if (!response.ok || result.type === "error") {
        setError(result.message || "Не удалось создать платёж");
        return;
      }

      if (result.confirmation?.confirmation_url) {
        window.open(result.confirmation.confirmation_url, "_blank");
      }
    } catch {
      setError("Не удалось создать платёж. Попробуйте позже.");
    } finally {
      setLoadingId(null);
    }
  };

  const handleSelectKeyType = (typeId) => {
    setSelectedKeyType(typeId);
    setError(null);
  };

  const handleBackToChoice = () => {
    setSelectedKeyType(null);
    setError(null);
    setFaqOpen(false);
  };

  return (
    <AppPageShell
      className="app-page--pricing"
      title="Тарифы"
      subtitle={
        selectedKeyType
          ? `Пакеты: ${activeKeyType?.title ?? ""}`
          : undefined
      }
    >
      <div
        className={`app-price-body${
          selectedKeyType ? " app-price-body--step2" : " app-price-body--step1"
        }`}
      >
        {selectedKeyType ? (
          <button
            type="button"
            className="app-price-back-btn"
            onClick={handleBackToChoice}
          >
            <span className="app-price-back-btn__icon">
              <ArrowBackIcon fontSize="small" />
            </span>
            <span>Назад</span>
          </button>
        ) : null}

        <div className="app-price-balance app-price-balance--compact">
          <div className="app-price-balance__item">
            <span className="app-price-balance__label">Одноразовые ключи</span>
            <span className="app-price-balance__value">{singleKeys}</span>
          </div>
          <div className="app-price-balance__item">
            <span className="app-price-balance__label">Групповой доступ</span>
            <span className="app-price-balance__value app-price-balance__value--text">
              {businessUntil
                ? `${businessPlan ?? "Активен"} до ${businessUntil}`
                : "Не подключён"}
            </span>
          </div>
        </div>

        {!selectedKeyType ? (
          <>
            <div className="app-price-step app-price-step--choice">
              <span className="app-price-step__label">Шаг 1 из 2</span>
            </div>

            <div className="app-price-types app-price-types--choice">
              {KEY_TYPES.map((type) => (
                <KeyTypeChoice
                  key={type.id}
                  type={type}
                  onSelect={handleSelectKeyType}
                />
              ))}
            </div>

            <div className="app-price-help-row">
              <span className="app-price-help-row__text">
                Какой тип доступа вам нужен?
              </span>
              <button
                type="button"
                className={`app-price-help-btn${
                  faqOpen ? " app-price-help-btn--active" : ""
                }`}
                onClick={() => setFaqOpen((open) => !open)}
                aria-expanded={faqOpen}
                aria-label="Частые вопросы"
                title="Частые вопросы"
              >
                <HelpOutlineIcon fontSize="small" />
              </button>
            </div>

            {faqOpen ? (
              <section className="app-price-faq app-price-faq--inline">
                <h2 className="app-price-faq__title">Частые вопросы</h2>
                <div className="app-price-faq__list">
                  {PRICING_FAQ.map((item) => (
                    <details className="app-price-faq__item" key={item.question}>
                      <summary>{item.question}</summary>
                      <p>{item.answer}</p>
                    </details>
                  ))}
                </div>
              </section>
            ) : null}
          </>
        ) : (
          <div className="app-price-packages">
            <div className="app-price-step app-price-step--packages">
              <span className="app-price-step__label">Шаг 2 из 2</span>
              <h2 className="app-price-step__title">{activeSection?.title}</h2>
              {activeSection?.subtitle ? (
                <p className="app-price-step__desc">{activeSection.subtitle}</p>
              ) : null}
            </div>

            {error ? <div className="app-price-error">{error}</div> : null}

            <div className="app-price-grid">
              {activeSection?.plans.map((plan) => (
                <PricingCard
                  key={plan.packageId}
                  plan={plan}
                  onBuy={handlePayment}
                  disabled={loadingId === plan.packageId}
                />
              ))}
            </div>
          </div>
        )}

      </div>

      <div className="app-price-note">
        <div>
          Оплата через{" "}
          <a href="https://yoomoney.ru" target="_blank" rel="noreferrer">
            ЮMoney
          </a>
          . Ключи зачисляются сразу, подписка — на 30 дней.
        </div>
        <div>Поддержка: support@club-lab.ru</div>
      </div>
    </AppPageShell>
  );
}
